import { signeDansLeChiffre } from '../schemas/sales.js';
import { jourMural } from '../agenda/fuseau.js';
import { cleDeTranche } from './series.js';
const deux = (n) => String(n).padStart(2, '0');
/** Dernier jour d'un mois (1-12), bissextile compris. */
const dernierJour = (annee, mois) => new Date(Date.UTC(annee, mois, 0)).getUTCDate();
/**
 * La période EN COURS arrêtée à aujourd'hui, et la période précédente arrêtée
 * au MÊME point.
 *
 * ⚠️ « Ce mois-ci » se compare au mois dernier À LA MÊME DATE, jamais au mois
 * dernier entier : le 10, dix jours de ventes face à trente-et-un annonçaient
 * une chute de 70 % qui n'existait pas. Même règle pour l'année.
 *
 * ⚠️ Pour le JOUR, aucune comparaison : les ventes ne portent qu'une date, pas
 * d'heure, et la matinée d'aujourd'hui face à toute la journée d'hier ferait
 * lire une baisse chaque matin. `precedent` vaut alors `null`.
 */
export function periodeEnCours(pas, aujourdhui) {
    const [a, m, j] = aujourdhui.split('-').map(Number);
    if (pas === 'jour')
        return { courante: { du: aujourdhui, au: aujourdhui }, precedent: null };
    if (pas === 'mois') {
        const am = m === 1 ? a - 1 : a;
        const mm = m === 1 ? 12 : m - 1;
        return {
            courante: { du: `${a}-${deux(m)}-01`, au: aujourdhui },
            precedent: {
                du: `${am}-${deux(mm)}-01`,
                // Le 31 mars se compare au 28 (ou 29) février, pas à un 31 inexistant.
                au: `${am}-${deux(mm)}-${deux(Math.min(j, dernierJour(am, mm)))}`,
            },
        };
    }
    return {
        courante: { du: `${a}-01-01`, au: aujourdhui },
        precedent: {
            du: `${a - 1}-01-01`,
            au: `${a - 1}-${deux(m)}-${deux(Math.min(j, dernierJour(a - 1, m)))}`,
        },
    };
}
/**
 * Variation en pourcentage, à une décimale — ou `null` quand elle n'a pas de
 * sens.
 *
 * ⚠️ `null` et non « +100 % » ni « +∞ » quand il n'y avait rien avant : un
 * premier mois d'activité n'a pas « doublé », il a commencé.
 */
export function variation(actuel, precedent) {
    if (precedent === 0)
        return null;
    return Math.round(((actuel - precedent) / Math.abs(precedent)) * 1000) / 10;
}
const dansPeriode = (date, p) => date >= p.du && date <= p.au;
/** Les ventes qui comptent dans le chiffre, sur une période. */
function ventesDe(ventes, p) {
    return ventes.filter((v) => signeDansLeChiffre(v) !== 0 && dansPeriode(v.issueDate, p));
}
/** Chiffre d'affaires TTC et passages en caisse d'une période — la règle des cartes. */
export function chiffreDe(ventes, p) {
    const retenues = ventesDe(ventes, p);
    const ca = retenues.reduce((s, v) => s + signeDansLeChiffre(v) * v.totalAmount, 0);
    const passages = retenues.filter((v) => signeDansLeChiffre(v) === 1).length;
    return { ca, ventes: passages, panierMoyen: passages > 0 ? Math.round(ca / passages) : null };
}
/**
 * Le chiffre d'une période, par CATÉGORIE d'article.
 *
 * ⚠️ Une ligne libre, ou dont l'article a disparu, tombe dans « Sans
 * catégorie » plutôt que de disparaître : l'anneau doit totaliser le chiffre,
 * sans quoi ses pourcentages mentiraient sur ce qui reste.
 */
export function repartitionParCategorie(ventes, categorieDeLArticle, nomDeLaCategorie, p) {
    const parCle = new Map();
    for (const v of ventesDe(ventes, p)) {
        const signe = signeDansLeChiffre(v);
        for (const l of v.lines) {
            const categorie = l.itemId ? (categorieDeLArticle.get(l.itemId) ?? null) : null;
            const cle = categorie && nomDeLaCategorie.has(categorie) ? categorie : 'sans-categorie';
            const part = parCle.get(cle) ?? {
                cle,
                libelle: cle === 'sans-categorie' ? 'Sans catégorie' : (nomDeLaCategorie.get(cle) ?? cle),
                montant: 0,
            };
            part.montant += signe * l.total;
            parCle.set(cle, part);
        }
    }
    return [...parCle.values()].filter((x) => x.montant > 0).sort((a, b) => b.montant - a.montant);
}
/**
 * Garde les `n - 1` premières parts et regroupe le reste en « Autres ».
 *
 * ⭐ Au-delà de cinq couleurs, un anneau ne se lit plus : on ne sait plus
 * quelle teinte est quelle catégorie. La queue se replie plutôt que de
 * réclamer une sixième couleur inventée.
 */
export function replier(parts, n) {
    if (parts.length <= n)
        return [...parts];
    const tete = parts.slice(0, n - 1);
    const reste = parts.slice(n - 1).reduce((s, x) => s + x.montant, 0);
    return [...tete, { cle: 'autres', libelle: 'Autres', montant: reste }];
}
/**
 * Les ENCAISSEMENTS d'une période, par moyen de paiement.
 *
 * ⚠️ Datés du jour où l'argent est ENTRÉ, pas du jour de la facture : un
 * acompte versé en août sur une facture de juillet est de l'argent d'août.
 * Et ce jour se lit à l'horloge de la BOUTIQUE.
 */
export function encaissementsParMoyen(ventes, p, fuseau) {
    const parMoyen = new Map();
    for (const v of ventes) {
        const signe = signeDansLeChiffre(v);
        if (signe === 0)
            continue;
        for (const r of v.payments) {
            if (!r.receivedAt || !dansPeriode(jourMural(r.receivedAt, fuseau), p))
                continue;
            parMoyen.set(r.method, (parMoyen.get(r.method) ?? 0) + signe * r.amount);
        }
    }
    return [...parMoyen.entries()]
        .map(([moyen, montant]) => ({ moyen, montant }))
        .filter((x) => x.montant > 0)
        .sort((a, b) => b.montant - a.montant);
}
/**
 * Les articles qui rapportent le plus sur la période, avec leur tendance.
 *
 * ⭐ Classés au MONTANT, pas à la quantité : dix bonbons ne pèsent pas une
 * coupe. Le libellé est celui de la dernière vente — il reste lisible même si
 * l'article a été supprimé depuis.
 */
export function meilleuresVentes(ventes, p, pas, decoupage, limite = 5) {
    const index = new Map(decoupage.map((t, i) => [t.cle, i]));
    const debut = decoupage[0]?.du ?? p.du;
    const fin = decoupage.at(-1)?.au ?? p.au;
    const parArticle = new Map();
    for (const v of ventes) {
        const signe = signeDansLeChiffre(v);
        if (signe === 0)
            continue;
        const dansP = dansPeriode(v.issueDate, p);
        const i = v.issueDate >= debut && v.issueDate <= fin
            ? index.get(cleDeTranche(v.issueDate, pas))
            : undefined;
        if (!dansP && i === undefined)
            continue;
        for (const l of v.lines) {
            if (!l.itemId)
                continue;
            const a = parArticle.get(l.itemId) ?? {
                itemId: l.itemId,
                libelle: l.label,
                quantite: 0,
                montant: 0,
                serie: decoupage.map(() => 0),
            };
            if (dansP) {
                a.quantite += signe * l.quantity;
                a.montant += signe * l.total;
                a.libelle = l.label || a.libelle;
            }
            if (i !== undefined)
                a.serie[i] = (a.serie[i] ?? 0) + signe * l.total;
            parArticle.set(l.itemId, a);
        }
    }
    return [...parArticle.values()]
        .filter((a) => a.montant > 0)
        .sort((a, b) => b.montant - a.montant)
        .slice(0, limite);
}
//# sourceMappingURL=tableau.js.map